import { createWordInputSchema, PARTS_OF_SPEECH } from "@rmnlng/shared";
import { useForm } from "@tanstack/react-form";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCreateWord } from "@/lib/words";

export function WordsNew() {
  const navigate = useNavigate();
  const create = useCreateWord();

  const form = useForm({
    defaultValues: {
      lemma: "",
      pos: "",
      gender: "",
      notes: "",
      translations: [""],
      examples: [] as Array<{ text: string; translation: string }>,
    },
    onSubmit: async ({ value }) => {
      const payload = createWordInputSchema.parse({
        lemma: value.lemma,
        language: "RO",
        pos: value.pos || undefined,
        gender: value.gender || undefined,
        notes: value.notes || undefined,
        translations: value.translations.filter((t) => t.trim()),
        examples: value.examples.filter((e) => e.text.trim() && e.translation.trim()),
      });
      const word = await create.mutateAsync(payload);
      navigate(`/words/${word.id}`, { replace: true });
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Link
          to="/words"
          aria-label="Back"
          className="inline-flex size-9 items-center justify-center rounded-full text-text-2 transition-colors hover:bg-[color:var(--surface-2)] hover:text-text"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <h1 className="text-[26px] font-bold tracking-[-0.02em]">Add word</h1>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <Section title="Base">
          <div className="flex flex-col gap-4">
            <form.Field
              name="lemma"
              validators={{
                onChange: z.string().trim().min(1, "Lemma is required"),
              }}
            >
              {(field) => (
                <div className="flex flex-col gap-2">
                  <Label htmlFor={field.name}>Lemma (Romanian)</Label>
                  <Input
                    id={field.name}
                    autoFocus
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder="e.g. pâine"
                  />
                  <FieldError errors={field.state.meta.errors} touched={field.state.meta.isTouched} />
                </div>
              )}
            </form.Field>

            <div className="grid grid-cols-2 gap-4">
              <form.Field name="pos">
                {(field) => (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor={field.name}>Part of speech</Label>
                    <Select
                      id={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                    >
                      <option value="">—</option>
                      {PARTS_OF_SPEECH.map((p) => (
                        <option key={p} value={p}>
                          {p.toLowerCase()}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}
              </form.Field>

              <form.Field name="gender">
                {(field) => (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor={field.name}>Gender</Label>
                    <Select
                      id={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                    >
                      <option value="">—</option>
                      <option value="M">masculine</option>
                      <option value="F">feminine</option>
                      <option value="N">neuter</option>
                    </Select>
                  </div>
                )}
              </form.Field>
            </div>

            <form.Field name="notes">
              {(field) => (
                <div className="flex flex-col gap-2">
                  <Label htmlFor={field.name}>Notes</Label>
                  <Textarea
                    id={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="e.g. pâinea (articulat)"
                  />
                </div>
              )}
            </form.Field>
          </div>
        </Section>

        <Section title="Translations (RU)">
          <form.Field name="translations" mode="array">
            {(field) => (
              <div className="flex flex-col gap-2">
                {field.state.value.map((_, i) => (
                  <div key={i} className="flex gap-2">
                    <form.Field name={`translations[${i}]`}>
                      {(sub) => (
                        <Input
                          value={sub.state.value}
                          onChange={(e) => sub.handleChange(e.target.value)}
                          placeholder="e.g. хлеб"
                        />
                      )}
                    </form.Field>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => field.removeValue(i)}
                      aria-label="Remove translation"
                    >
                      <Trash2 />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => field.pushValue("")}
                  className="self-start"
                >
                  <Plus />
                  Add translation
                </Button>
              </div>
            )}
          </form.Field>
        </Section>

        <Section title="Examples">
          <form.Field name="examples" mode="array">
            {(field) => (
              <div className="flex flex-col gap-3">
                {field.state.value.map((_, i) => (
                  <div
                    key={i}
                    className="flex flex-col gap-2 rounded-[var(--r-md)] border border-border bg-[color:var(--surface-2)] p-3"
                  >
                    <form.Field name={`examples[${i}].text`}>
                      {(sub) => (
                        <Input
                          value={sub.state.value}
                          onChange={(e) => sub.handleChange(e.target.value)}
                          placeholder="Romanian sentence"
                        />
                      )}
                    </form.Field>
                    <form.Field name={`examples[${i}].translation`}>
                      {(sub) => (
                        <Input
                          value={sub.state.value}
                          onChange={(e) => sub.handleChange(e.target.value)}
                          placeholder="Russian translation"
                        />
                      )}
                    </form.Field>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => field.removeValue(i)}
                      className="self-end"
                    >
                      <Trash2 />
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => field.pushValue({ text: "", translation: "" })}
                  className="self-start"
                >
                  <Plus />
                  Add example
                </Button>
              </div>
            )}
          </form.Field>
        </Section>

        {create.error && (
          <p className="text-[13px] text-[color:var(--again)]" role="alert">
            {create.error.message}
          </p>
        )}

        <div className="flex gap-2 pt-2">
          <form.Subscribe
            selector={(s) => ({
              canSubmit: s.canSubmit,
              isSubmitting: s.isSubmitting,
            })}
          >
            {({ canSubmit, isSubmitting }) => (
              <Button type="submit" size="block" disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? "Saving…" : "Save word"}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-[var(--r-lg)] border border-border bg-surface p-4">
      <div className="mb-3 text-[13px] font-semibold">{title}</div>
      {children}
    </section>
  );
}

function FieldError({
  errors,
  touched,
}: {
  errors: Array<{ message?: string } | string | undefined>;
  touched: boolean;
}) {
  if (!touched || errors.length === 0) return null;
  const message = errors
    .map((e) => (typeof e === "string" ? e : e?.message))
    .filter(Boolean)
    .join(", ");
  if (!message) return null;
  return (
    <p className="text-[12px] text-[color:var(--again)]" role="alert">
      {message}
    </p>
  );
}
